import jsonServerProvider from 'ra-data-json-server';
import { withLifecycleCallbacks } from 'react-admin';

// For Analytics custom call
import { fetchUtils } from 'ra-core';
import { stringify } from 'query-string';

// Group ID enforced in all queries
const RESOURCED_FILTERED_BY_GROUP_ID = [
	'libraries', 'topics', 'documents', 'orbs'
];

// This presumes that useStore() continues to use this mechanism.
function getActiveGroupId() {
	return localStorage.getItem( 'RaStore.activeGroupId' );
}

function ensureReadWithGroupId( params, dataProvider, resource ) {
	//console.log( arguments );
	if ( ! RESOURCED_FILTERED_BY_GROUP_ID.includes( resource ) ) {
		return params;
	}

	const group_id = getActiveGroupId();
	if ( params.filter && group_id ) {
		params.filter.group_id = group_id;
	}

	return params;
}

function ensureWriteWithGroupId( params, dataProvider, resource ) {
	if ( ! RESOURCED_FILTERED_BY_GROUP_ID.includes( resource ) ) {
		return params;
	}

	//console.log( params );
	return params;
}

const apiUrl = '/api';
const httpClient = fetchUtils.fetchJson;

function handleUploadedFile( fileField ) {
	return async ( params ) => {

		let files = params.data[fileField];
		if ( ! Array.isArray( files ) ) {
			files = [ files ];
		}

		const newFiles = files.filter( f => f.rawFile instanceof File );
		const curFiles = files.filter( f => !(f.rawFile instanceof File ) );
		const base64Files = await Promise.all( newFiles.map( convertFileToBase64 ) );

		const documents = [
			...base64Files.map( (src, index) => ({
				...newFiles[index],
				src
			})),
			...curFiles
		];

		return {
			...params,
			data: {
				...params.data,
				documents
			}
		};
	};
}

function convertFileToBase64( file ) {
	return new Promise( (resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve( reader.result );
		reader.onerror = reject;
		reader.readAsDataURL( file.rawFile );
	});
}

// Resource: { propertyWithObjects => propertyWithIDs }
const associatedRecordMap = {
	topics: {
		libraries: 'library_ids'
	},
	libraries: {
		topics: 'topic_ids'
	},
	groups: {
		users: 'user_ids'
	},
	users: {
		groups: 'group_ids'
	}
};

/**
 * Transform nested objects to ID arrays for React Admin inputs
 * Converts: { libraries: [{id: 1, name: "Lib1"}] }
 * To: { libraries: [...], library_ids: [1] }
 */
function translateAssociations(response, resource) {
	const map = associatedRecordMap[resource];
	if (!map) return response;

	const transformRecord = (record) => {
		const transformed = { ...record };

		Object.entries(map).forEach(([objectKey, idKey]) => {
			if (record[objectKey]) {
				// Convert array of objects to array of IDs
				transformed[idKey] = record[objectKey].map(item => item.id || item);
			}
		});

		return transformed;
	};

	if (response.data) {
		if (Array.isArray(response.data)) {
			response.data = response.data.map(transformRecord);
		} else {
			response.data = transformRecord(response.data);
		}
	}

	return response;
}

/**
 * Transform ID arrays back to what backend expects
 * Converts: { library_ids: [1, 2, 3] }
 * To: { libraries: [1, 2, 3] } (backend handles both formats)
 */
function mapAssociations(params, resource) {
	const map = associatedRecordMap[resource];
	if (!map || !params.data) return params;

	const transformed = { ...params };

	Object.entries(map).forEach(([objectKey, idKey]) => {
		if (params.data[idKey] !== undefined) {
			// Send as the object field name (backend accepts both formats)
			transformed.data[objectKey] = params.data[idKey];
		}
	});

	return transformed;
}

export const dataProvider = withLifecycleCallbacks( jsonServerProvider('/api'), [
	{
		resource: 'topics',
		beforeCreate: async (params, dataProvider) => {
			params = await handleUploadedFile( 'documents' )(params);
			return mapAssociations(params, 'topics');
		},
		beforeUpdate: async (params, dataProvider) => {
			params = await handleUploadedFile( 'documents' )(params);
			return mapAssociations(params, 'topics');
		},
		beforeDelete: (params) => {
			// Add confirm query parameter if meta.confirm is true
			if (params.meta?.confirm) {
				return {
					...params,
					meta: {
						...params.meta,
						query: { confirm: 'true' }
					}
				};
			}
			return params;
		}
	},

	{
		resource: '*',

		beforeGetList: ensureReadWithGroupId,
		beforeGetOne: ensureReadWithGroupId,
		beforeGetMany: ensureReadWithGroupId,
		beforeGetManyReference: ensureReadWithGroupId,

		beforeCreate: async (params, dataProvider, resource) => {
			params = ensureWriteWithGroupId(params, dataProvider, resource);
			return mapAssociations(params, resource);
		},
		beforeUpdate: async (params, dataProvider, resource) => {
			params = ensureWriteWithGroupId(params, dataProvider, resource);
			return mapAssociations(params, resource);
		},
		beforeUpdateMany: ensureWriteWithGroupId,

		beforeDelete: ensureWriteWithGroupId,
		beforeDeleteMany: ensureWriteWithGroupId,

		// Transform nested objects to ID arrays after fetching
		afterGetOne: translateAssociations,
		afterGetList: translateAssociations,
		afterGetMany: translateAssociations
	}

]);

const buildAPIUrl = ( resource, query ) => {
	return `${apiUrl}/${resource}?${stringify(query)}`;
};

// Utility functions for custom API calls
const getRequest = async ( path ) => {
	const url = buildAPIUrl( path );
	const { json } = await httpClient( url );
	return json;
};

const postRequest = async ( path, body = null ) => {
	const url = buildAPIUrl( path );
	const args = {
		method: 'POST',
		body: body ? JSON.stringify( body ) : null
	};
	const { json } = await httpClient( url, args );
	return json;
};

const deleteRequest = async ( path ) => {
	const url = buildAPIUrl( path );
	const args = {
		method: 'DELETE'
	};
	const { json } = await httpClient( url, args );
	return json;
};

// Document operations
dataProvider.refreshDocumentsContent = async ( { ids, setReindexingStatus = false } ) => {
	const data = await postRequest( 'documents/refresh-content', { ids, setReindexingStatus } );
	return { data };
};

dataProvider.reindexDocument = async ( document_id ) => {
	return await postRequest( `documents/${document_id}/reindex` );
};

// Topic operations
dataProvider.reindexTopic = async ( topic_id ) => {
	return await postRequest( `topics/${topic_id}/reindex` );
};

dataProvider.clearTopicIndex = async ( topic_id ) => {
	return await deleteRequest( `topics/${topic_id}/clear-index` );
};

// Library operations
dataProvider.reindexLibrary = async ( library_id ) => {
	return await postRequest( `libraries/${library_id}/reindex` );
};

dataProvider.clearLibraryIndex = async ( library_id ) => {
	return await deleteRequest( `libraries/${library_id}/clear-index` );
};

// Stats & Analytics operations
dataProvider.getIndexingStats = async ( group_id ) => {
	return await getRequest( `stats/indexing-stats?group_id=${group_id}` );
};

dataProvider.getIndexHealth = async () => {
	return await getRequest( 'stats/index-health' );
};

export default dataProvider;